import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Inbox, Sparkles, RefreshCw, Eye } from "lucide-react";
import { Card, CardBody } from "../components/Card";
import { api } from "../lib/api";
import type { ReviewItem } from "../lib/me";
import { Pagination } from "../components/Pagination";

export default function Review() {
  const navigate = useNavigate();
  const [items, setItems] = useState<ReviewItem[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);

  const loadData = async () => {
    setLoading(true);
    try {
      const reviewData = await api.get<ReviewItem[]>("/api/review");
      setItems(reviewData);
      setPage(0);
    } catch {
      setItems([]);
      setPage(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  if (items === null) {
    return (
      <div className="flex h-60 items-center justify-center">
        <RefreshCw className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold tracking-tight text-slate-900 sm:text-2xl">Review queue</h2>
          <p className="mt-1 text-sm text-slate-500 font-medium">
            Drafts generated by the AI, waiting for you to approve, edit, or reject.
          </p>
        </div>
        <button
          onClick={loadData}
          disabled={loading}
          className="self-start sm:self-center inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-bold text-slate-700 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 active:scale-[0.98] transition-all disabled:opacity-50 select-none cursor-pointer"
        >
          <RefreshCw className={cn("h-3.5 w-3.5", loading && "animate-spin")} />
          Refresh Queue
        </button>
      </div>

      {items.length === 0 ? (
        <Card>
          <CardBody>
            <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-100 border border-slate-200/60 text-slate-400">
                <Inbox className="h-5.5 w-5.5" />
              </div>
              <p className="mt-4 text-base font-bold text-slate-900">Inbox zero</p>
              <p className="mt-1.5 max-w-sm text-sm text-slate-500 font-medium leading-normal">
                Nothing waiting for review right now. New drafts will appear here as the AI
                processes inbound email.
              </p>
            </div>
          </CardBody>
        </Card>
      ) : (
        <div className="space-y-4">
          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[700px] border-collapse text-left">
                <thead>
                  <tr className="bg-slate-900 border-b border-slate-950 text-white select-none">
                    <th className="px-6 py-3.5 text-[11px] font-bold uppercase tracking-wider first:rounded-tl-2xl">Subject</th>
                    <th className="px-6 py-3.5 text-[11px] font-bold uppercase tracking-wider">Customer</th>
                    <th className="px-6 py-3.5 text-[11px] font-bold uppercase tracking-wider">Category & Match</th>
                    <th className="px-6 py-3.5 text-[11px] font-bold uppercase tracking-wider text-right">Updated</th>
                    <th className="px-6 py-3.5 text-[11px] font-bold uppercase tracking-wider last:rounded-tr-2xl text-right w-16">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {items.slice(page * limit, (page + 1) * limit).map((it) => (
                    <React.Fragment key={it.caseId}>
                      {/* Main Item Row */}
                      <tr
                        onClick={() => navigate(`/cases/${it.caseId}`)}
                        className="group cursor-pointer hover:bg-slate-50/50 transition-colors"
                      >
                        <td className="px-6 py-3.5 min-w-0 max-w-[240px]">
                          <h3 className="truncate text-sm font-semibold text-slate-900 group-hover:text-brand-600 transition-colors">
                            {it.subject}
                          </h3>
                        </td>
                        <td className="px-6 py-3.5 min-w-0 max-w-[220px]">
                          <p className="truncate text-sm font-semibold text-slate-800">
                            {it.fromName ?? it.fromEmail}
                          </p>
                          <p className="truncate text-xs font-normal text-slate-400 mt-0.5">
                            {it.fromEmail}
                          </p>
                        </td>
                        <td className="px-6 py-3.5">
                          <div className="flex items-center flex-wrap gap-1.5">
                            {it.intentName ? (
                              <span className="rounded-md bg-brand-50 border border-brand-200/40 px-2 py-0.5 text-[11px] font-medium text-brand-700 truncate max-w-full">
                                {it.intentName}
                              </span>
                            ) : (
                              <span className="text-[11px] text-slate-400 font-medium italic">Unclassified</span>
                            )}
                            {it.classificationConfidence != null && (
                              <span className="rounded-md bg-emerald-50 border border-emerald-200/50 px-2 py-0.5 text-[11px] font-medium text-emerald-700">
                                {Math.round(it.classificationConfidence * 100)}% Match
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-3.5 text-right">
                          <span className="text-xs font-medium text-slate-500">
                            {formatDateTime(it.updatedAt)}
                          </span>
                        </td>
                        <td className="px-6 py-3.5 text-right w-16">
                          <button className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-brand-50 hover:bg-brand-100 text-brand-600 transition-colors select-none cursor-pointer">
                            <Eye className="h-4 w-4 shrink-0" />
                          </button>
                        </td>
                      </tr>
                      
                      {/* Nested Draft Snippet Row */}
                      {it.draft && (
                        <tr className="bg-slate-50/10">
                          <td colSpan={5} className="px-6 pb-4 pt-0">
                            <Link to={`/cases/${it.caseId}`} className="block">
                              <div className="rounded-xl border border-brand-100 bg-brand-50/15 p-3.5 hover:border-brand-200/60 transition-colors">
                                <div className="flex items-center gap-1.5 text-[10px] font-bold text-brand-700 uppercase tracking-wider">
                                  <Sparkles className="h-3.5 w-3.5 animate-pulse" />
                                  AI Draft Response Preview
                                </div>
                                <p className="mt-1.5 line-clamp-2 whitespace-pre-wrap text-xs font-medium text-slate-555 leading-relaxed">
                                  {it.draft.draftedBody}
                                </p>
                              </div>
                            </Link>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          <Pagination
            currentPage={page}
            totalRecords={items.length}
            limit={limit}
            loading={loading}
            onPageChange={(p) => setPage(p)}
            onLimitChange={(l) => {
              setLimit(l);
              setPage(0);
            }}
          />
        </div>
      )}
    </div>
  );
}

function cn(...classes: any[]) {
  return classes.filter(Boolean).join(" ");
}

function formatDateTime(dateInput: string | Date): string {
  const d = new Date(dateInput);
  if (isNaN(d.getTime())) return "";
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  let hours = d.getHours();
  const minutes = String(d.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12;
  hours = hours ? hours : 12;
  const hoursStr = String(hours).padStart(2, "0");
  return `${year}-${month}-${day} ${hoursStr}:${minutes} ${ampm}`;
}
